import type { Action } from '@ngrx/store';

import { inject, Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { switchMap } from 'rxjs/operators';

import { WebWidgetsFactoryService } from '@web/core/services/web-widgets-factory.service';

import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';

import WidgetsLegacyActions from '@web/store/page-structure/widgets/widgets.actions';
import GridsActions from './grids.actions';

@Injectable()
export class GridsEffects {
    private readonly actions$: Actions = inject(Actions);
    private readonly gridFacade: GridsFacade = inject(GridsFacade);

    public readonly addGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GridsActions.add),
            switchMap(({ entity, order }) => {
                return [
                    GridsActions.added({
                        entity: {
                            ...entity,
                            children: entity.children.map((widgets) => widgets.map((w) => w.id)),
                        },
                        order,
                    }),
                    WidgetsLegacyActions.addManyWidgetsToGrid({ widgets: entity.children.flat() }),
                ];
            })
        )
    );

    public readonly createGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GridsActions.create),
            switchMap(({ parentId, order, variant = 0, skipHistory }) => {
                const newGridInstance = this.gridFacade.getGridInstanceValue(parentId, variant);
                const actions: Action[] = [GridsActions.created({ entity: newGridInstance, order, skipHistory })];

                return actions;
            })
        )
    );

    public readonly copyGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GridsActions.copy),
            switchMap(({ entity, order }) => {
                const gridCopy = WebWidgetsFactoryService.copyGrid(entity);

                const children = gridCopy.children.map((row) =>
                    row.map((widget) => WebWidgetsFactoryService.copyWidgetIntoGrid(widget, gridCopy.id))
                );

                return [
                    WidgetsLegacyActions.copyManyWidgets({ widgets: children.flat() }),
                    GridsActions.copied({
                        entity: { ...gridCopy, children: children.map((row) => row.map((w) => w.id)) },
                        order,
                    }),
                ];
            })
        )
    );
}
